import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Chip,
  Button,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';
import AppDataGrid from '../../common/AppDataGrid';
import StockAdjustmentDialog from './StockAdjustmentDialog';
import { useCanUpdate } from '../../common/activeToggle';
import TableSearch from '../../common/TableSearch';
import { useDataTable } from '../../../hooks/useDataTable';
import { useStore } from '../../../context/StoreContext';

// The backend isn't consistent about movementType casing — "Purchase"/"Sale" are
// written mixed-case (OrderService/PurchaseOrderService), "ADJUSTMENT" is uppercase
// (StockMovementService), and DAMAGE/RETURN/TRANSFER come through a free-text field
// with no enum validation. Normalize before comparing/translating so the filter and
// the Chip label work regardless of what casing a given movement was written with.
const normalizeMovementType = (type) => (type || '').toUpperCase();

const StockMovementsList = () => {
  const { t } = useTranslation();
  const { currentStore } = useStore();
  const canAdjust = useCanUpdate('stock.update');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Page, size, search, sort and the type filter live in the URL
  // (/stock-movements?page=0&size=20&movementType=SALE&sort=createdAt,desc). Refetches when the store changes.
  const table = useDataTable(productService.getStockMovementsPage, {}, {
    onError: () => toast.error(t('stockMovements.fetchError')),
    deps: [currentStore],
  });
  const movementTypeFilter = (table.params.movementType || 'ALL').toUpperCase();

  const movementTypes = [
    { value: 'ALL', label: t('stockMovements.types.ALL') },
    { value: 'PURCHASE', label: t('stockMovements.types.PURCHASE') },
    { value: 'SALE', label: t('stockMovements.types.SALE') },
    { value: 'ADJUSTMENT', label: t('stockMovements.types.ADJUSTMENT') },
    { value: 'DAMAGE', label: t('stockMovements.types.DAMAGE') },
    { value: 'RETURN', label: t('stockMovements.types.RETURN') },
  ];

  const getMovementTypeColor = (type) => {
    switch (normalizeMovementType(type)) {
      case 'PURCHASE':
        return 'success';
      case 'SALE':
        return 'info';
      case 'ADJUSTMENT':
        return 'warning';
      case 'DAMAGE':
        return 'error';
      case 'RETURN':
        return 'primary';
      default:
        return 'default';
    }
  };

  const columns = [
    { field: 'id', headerName: t('common.id'), width: 70, sortable: false },
    {
      field: 'createdAt',
      headerName: t('common.date'),
      width: 180,
      cellRenderer: (params) => new Date(params.value).toLocaleString(),
    },
    { field: 'productName', headerName: t('stockMovements.product'), width: 200 },
    { field: 'variantName', headerName: t('stockMovements.variant'), width: 250 },
    { field: 'variantSku', headerName: t('products.sku'), width: 120 },
    {
      field: 'movementType',
      headerName: t('stockMovements.movementType'),
      width: 130,
      cellRenderer: (params) => (
        <Chip
          label={t(`stockMovements.types.${normalizeMovementType(params.value)}`, params.value)}
          color={getMovementTypeColor(params.value)}
          size="small"
        />
      ),
      // Show the same translated label the Chip displays — the raw backend value
      // (params.value) is inconsistently-cased English, not what the user sees.
      tooltipValueGetter: (params) =>
        t(`stockMovements.types.${normalizeMovementType(params.value)}`, params.value),
    },
    {
      field: 'quantityChange',
      headerName: t('stockMovements.quantityChange'),
      width: 120,
      cellRenderer: (params) => (
        <Typography
          variant="body2"
          fontWeight={600}
          color={params.value > 0 ? 'success.main' : 'error.main'}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
        >
          {params.value > 0 ? '+' : ''}{params.value}
        </Typography>
      ),
    },
    {
      field: 'referenceType',
      headerName: t('stockMovements.referenceType'),
      width: 150,
      cellRenderer: (params) => params.value || 'N/A',
    },
    { field: 'createdByUserName', headerName: t('stockMovements.createdBy'), width: 150, sortable: false },
    {
      field: 'notes',
      headerName: t('common.notes'),
      width: 250,
      sortable: false,
      cellRenderer: (params) => params.value || '-',
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('stockMovements.title')}</Typography>
        {canAdjust && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            {t('stockMovements.newMovement')}
          </Button>
        )}
      </Box>

      <StockAdjustmentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={table.refresh}
      />

      <Card>
        <CardContent>
          <TableSearch
            placeholder={t('common.search')}
            value={table.searchInput}
            onChange={table.handleSearchChange}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 3 }}>
            <TextField
              select
              label={t('stockMovements.movementType')}
              value={movementTypeFilter}
              onChange={(e) => table.updateParams({ movementType: e.target.value })}
              sx={{ minWidth: 200 }}
            >
              {movementTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <AppDataGrid
            rows={table.data.content}
            columns={columns}
            loading={table.loading}
            paginationMode="server"
            rowCount={table.data.totalElements}
            paginationModel={table.paginationModel}
            onPaginationModelChange={table.handlePaginationModelChange}
            sortModel={table.sortModel}
            onSortModelChange={table.handleSortChange}
            pageSizeOptions={[10, 20, 50, 100]}
            getRowId={(row) => row.id}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default StockMovementsList;
